/**
 * AuditHistory — Displays the audit trail for a time entry.
 * Sprint 10, SDD §11.4, FR-EDIT-06, FR-EDIT-07
 */

import { useState, useEffect } from 'react';
import { apiFetch } from '../lib/api';

interface AuditEntry {
  id: number;
  action: string;
  editor_id: number | null;
  editor_name: string | null;
  old_values: Record<string, unknown> | null;
  new_values: Record<string, unknown> | null;
  created_at: string;
}

interface AuditResponse {
  entry_id: number;
  data: AuditEntry[];
}

interface AuditHistoryProps {
  entryId: number;
  onClose: () => void;
}

function formatTimestamp(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function actionLabel(action: string): string {
  switch (action) {
    case 'clock_in':
      return 'Clocked In';
    case 'clock_out':
      return 'Clocked Out';
    case 'edit_entry':
      return 'Edited';
    case 'void_entry':
      return 'Voided';
    default:
      return action;
  }
}

function formatFieldName(field: string): string {
  return field.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatFieldValue(value: unknown): string {
  if (value === null || value === undefined) return '(none)';
  return String(value);
}

function FieldChanges({
  oldValues,
  newValues,
}: {
  oldValues: Record<string, unknown>;
  newValues: Record<string, unknown>;
}) {
  return (
    <div className="flex flex-col gap-1">
      {Object.keys(newValues).map((field) => (
        <div key={field} className="text-xs">
          <span className="text-content-tertiary">{formatFieldName(field)}: </span>
          <span className="text-content-secondary line-through">{formatFieldValue(oldValues[field])}</span>
          <span className="text-content-tertiary"> → </span>
          <span className="text-content-primary">{formatFieldValue(newValues[field])}</span>
        </div>
      ))}
    </div>
  );
}

export default function AuditHistory({ entryId, onClose }: AuditHistoryProps) {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function fetchAudit() {
      try {
        const data = await apiFetch<AuditResponse>(`/api/audit/${entryId}`);
        if (!cancelled) setEntries(data.data);
      } catch (err: unknown) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load audit history');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchAudit();
    return () => {
      cancelled = true;
    };
  }, [entryId]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Audit history"
      className="fixed inset-0 flex items-center justify-center bg-black/70 z-[1000] p-6"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-[440px] max-h-[80vh] overflow-auto bg-surface-raised rounded-2xl border border-edge-default p-6 shadow-2xl">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-bold text-action-primary m-0">Edit History</h2>
          <button
            type="button"
            onClick={onClose}
            className="bg-transparent border-none text-content-secondary text-xl cursor-pointer px-2 py-1"
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        {loading && <div className="py-5 text-center text-content-tertiary text-sm">Loading history...</div>}

        {error && (
          <div className="px-3.5 py-2.5 bg-red-500/15 border border-red-500/30 rounded-md text-red-300 text-[13px]">
            {error}
          </div>
        )}

        {!loading && !error && entries.length === 0 && (
          <div className="py-5 text-center text-content-tertiary text-sm">No audit history found.</div>
        )}

        {!loading && !error && entries.length > 0 && (
          <div className="flex flex-col gap-3">
            {entries.map((audit) => (
              <div
                key={audit.id}
                className={`px-3.5 py-2.5 bg-surface-base rounded-lg ${
                  audit.action === 'void_entry'
                    ? 'border-l-[3px] border-l-red-500'
                    : 'border-l-[3px] border-l-edge-default'
                }`}
              >
                <div className="flex justify-between items-baseline mb-1.5">
                  <span
                    className={`text-[13px] font-semibold ${audit.action === 'void_entry' ? 'text-red-300' : 'text-content-primary'}`}
                  >
                    {actionLabel(audit.action)}
                  </span>
                  <span className="text-[11px] text-content-tertiary">{formatTimestamp(audit.created_at)}</span>
                </div>

                {audit.editor_name && (
                  <div className="text-xs text-content-secondary mb-1.5">by {audit.editor_name}</div>
                )}

                {audit.action === 'edit_entry' && audit.old_values && audit.new_values && (
                  <FieldChanges oldValues={audit.old_values} newValues={audit.new_values} />
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
